import { CmpInvoiceDisplayStateEnum, CmpInvoiceKindEnum, GetCmpManageInvoicesQuery } from "../../../../graphql/types";
import { getCreditedByRowLinkLines, getCreditNoteRowLinkLines } from "../invoiceCreditLinks";
import { isInvoicePastDueOnChamberDate } from "../invoiceDueDate";
import { getRecordedPaymentMethodLabel } from "../invoiceFormatting";
import {
  isCreditedInvoiceDisplayState,
  isOutstandingInvoiceDisplayState,
} from "./manageInvoiceActions";
import { getManageInvoiceOverdueAgingText } from "./manageInvoiceStatusFilter";

// Manage Invoices Status column text: the status line and the lines under it.
type InvoiceRow =
  GetCmpManageInvoicesQuery["getCmpManageInvoices"]["rows"][number];

const getDisplayStateLabel = (state: CmpInvoiceDisplayStateEnum) => {
  switch (state) {
    case CmpInvoiceDisplayStateEnum.Draft:
      return "Draft";
    case CmpInvoiceDisplayStateEnum.Paid:
      return "Paid";
    case CmpInvoiceDisplayStateEnum.Refunded:
    case CmpInvoiceDisplayStateEnum.Credited:
      return "Credited";
    case CmpInvoiceDisplayStateEnum.Voided:
      return "Voided";
    case CmpInvoiceDisplayStateEnum.Waived:
      return "Waived";
    case CmpInvoiceDisplayStateEnum.PaymentFailed:
      return "Payment failed";
    case CmpInvoiceDisplayStateEnum.PaymentAbandoned:
      return "Abandoned";
    case CmpInvoiceDisplayStateEnum.PaymentExpired:
      return "Expired";
    case CmpInvoiceDisplayStateEnum.ActionRequired:
      return "Action required";
    case CmpInvoiceDisplayStateEnum.NotPaid:
    case CmpInvoiceDisplayStateEnum.AwaitingPayment:
      return "Awaiting Payment";
    case CmpInvoiceDisplayStateEnum.Overdue:
      return "Overdue";
    default:
      return "Unknown";
  }
};

const invoiceStatusTextClassName = "text-[#0A0A0A]";

// Design (20) CMP Dashboard.dc.html:14332: a settled $0 ticket invoice shows
// "Complimentary" (the Type column already says "Complimentary Ticket").
// Same rule as the backend "complimentary" status filter.
export const isComplimentaryTicketInvoiceRow = (
  row: Pick<InvoiceRow, "displayState" | "invoiceKind" | "totalAmountInCents">,
) =>
  row.invoiceKind === CmpInvoiceKindEnum.EventRegistration &&
  row.totalAmountInCents === 0 &&
  (row.displayState === CmpInvoiceDisplayStateEnum.Paid ||
    row.displayState === CmpInvoiceDisplayStateEnum.Waived);

// A source invoice row adds "Credited by #N" under its unchanged status.
export const getRowStatusCopy = (row: InvoiceRow, timeZone: string) => ({
  ...getRowBaseStatusCopy(row, timeZone),
  // Credit-note links (Oct 8 2026): a credit note names the invoice it
  // credits; a source invoice names its credit notes. Status is unchanged.
  linkLines: [
    ...getCreditNoteRowLinkLines(row.creditForInvoiceNumber),
    ...getCreditedByRowLinkLines(row.creditedByInvoiceNumbers),
  ],
});

const getRowBaseStatusCopy = (row: InvoiceRow, timeZone: string) => {
  const displayState = row.displayState;

  if (row.displayState === CmpInvoiceDisplayStateEnum.Draft) {
    return {
      className: invoiceStatusTextClassName,
      primary: "Draft",
      secondary: "",
    };
  }

  if (isOutstandingInvoiceDisplayState(displayState)) {
    const isOverdue =
      displayState === CmpInvoiceDisplayStateEnum.Overdue ||
      isInvoicePastDueOnChamberDate({
        dueAtUtcMilli: row.dueAtUtcMilli,
        timeZone,
      });

    if (isOverdue) {
      return {
        className: invoiceStatusTextClassName,
        primary: "Awaiting Payment",
        secondary: getManageInvoiceOverdueAgingText(row.daysPastDue),
      };
    }

    return {
      className: invoiceStatusTextClassName,
      primary: "Awaiting Payment",
      secondary: row.paymentPlan?.enabled
        // Design (16) INV-1045 spelling: "0/12 Instalments".
        ? `${row.paymentPlan.paidInstallments}/${row.paymentPlan.totalInstallments} Instalments`
        : "",
    };
  }

  if (isComplimentaryTicketInvoiceRow(row)) {
    return {
      className: invoiceStatusTextClassName,
      primary: "Complimentary",
      secondary: "",
    };
  }

  if (row.displayState === CmpInvoiceDisplayStateEnum.Paid) {
    return {
      className: invoiceStatusTextClassName,
      primary: "Paid",
      secondary: getRecordedPaymentMethodLabel(row.paymentMethodLabel),
    };
  }

  if (isCreditedInvoiceDisplayState(displayState)) {
    return {
      className: invoiceStatusTextClassName,
      primary: "Credited",
      secondary: "Refunded",
    };
  }

  if (row.displayState === CmpInvoiceDisplayStateEnum.Voided) {
    return {
      className: invoiceStatusTextClassName,
      primary: "Voided",
      secondary: "",
    };
  }

  return {
    className: invoiceStatusTextClassName,
    primary: getDisplayStateLabel(row.displayState),
    secondary: "",
  };
};
